import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Button from '../../../../../shared/_TBComponents/Button';
import Modal from '../../../../../shared/_TBComponents/Modal';
import SelectBox from '../../../../../shared/_TBComponents/SelectBox';
import Text from '../../../../../shared/_TBComponents/Text';
import { showSuccessToast, showErrorToast } from '../../../../../shared/utils/toastManager';
import { complexPasswordRegex } from '../../../../../shared/utils/registrationSchemaHelper';
import {
  createCoach, fetchUser, updateCoach,
} from '../../../ducks/navigatorActions';
import CalendarPicker from '../CalendarPicker';
import UserProfileFields, { Row, FieldWrapper } from '../forms/UserProfileFields';
import ConfirmModal from './ConfirmModal';

const DEFAULT_PIC = process.env.ENVIRONMENT === 'PRODUCTION'
  ? 'https://s3.amazonaws.com/teambuildr-production-assets/pic/default.png'
  : 'https://s3.amazonaws.com/teambuildr-staging-assets/pic/default.png';

const GROUP_ACCESS_OPTIONS = [
  { id: '0', name: 'Allow access to all groups' },
  { id: '1', name: 'Access to selected groups only' },
  { id: '2', name: 'Disable all group access' },
];

const CALENDAR_ACCESS_OPTIONS = [
  { id: '0', name: 'Allow access to all calendars' },
  { id: '1', name: 'Access to selected calendars' },
  { id: '2', name: 'Disable all calendar access' },
];

const ACCESS_RESTRICTION_FIELDS = [
  { key: 'attendance', label: 'Attendance', visibilityKey: 'attendanceEnabled' },
  { key: 'exercises', label: 'Exercise Manager' },
  { key: 'feed', label: 'Feed', visibilityKey: 'feedEnabled' },
  { key: 'manageUsers', label: 'Manage Users' },
  { key: 'reporting', label: 'Reporting', visibilityKey: 'reportingEnabled' },
  { key: 'onlinePayments', label: 'Online Payments', visibilityKey: 'resellerEnabled' },
  { key: 'billing', label: 'Billing' },
  { key: 'gymStudio', label: 'TeamBuildr OS' },
];

const SELECTBOX_STYLES = {
  control: (provided, state) => ({
    ...provided,
    fontFamily: 'Nunito Sans',
    fontSize: '14px',
    fontWeight: 600,
    borderColor: state.isFocused ? '#77A7FF' : '#D0D0D0',
    boxShadow: 'none',
    '&:hover': { borderColor: state.isFocused ? '#77A7FF' : '#D0D0D0' },
  }),
  option: (provided, state) => ({
    ...provided,
    fontFamily: 'Nunito Sans',
    fontSize: '14px',
    fontWeight: 600,
    backgroundColor: state.isFocused || state.isSelected ? 'rgba(119, 167, 255, 0.15)' : 'transparent',
    color: '#333333',
    '&:active': { backgroundColor: 'rgba(119, 167, 255, 0.25)' },
  }),
};

const buildEditInitialValues = (
  user,
  usernamesEnabled,
  allGroups,
  allCalendars,
  accountCalendars,
) => {
  const groupMap = Object.fromEntries((allGroups || []).map((g) => [g.id, g]));
  const calendarMap = Object.fromEntries((allCalendars || []).map((c) => [c.id, c]));
  const accountCalendarsMap = Object.fromEntries(
    (accountCalendars || []).map((c) => [c.id, c]),
  );

  const getGroupAccessOption = () => {
    if (user.userGroupIds === null) return GROUP_ACCESS_OPTIONS[0];
    if (Array.isArray(user.userGroupIds)
      && user.userGroupIds.length === 0) return GROUP_ACCESS_OPTIONS[2];
    if (Array.isArray(user.userGroupIds)
      && user.userGroupIds.length > 0) return GROUP_ACCESS_OPTIONS[1];
    return GROUP_ACCESS_OPTIONS[0];
  };

  const getCalendarAccessOption = () => {
    if (user.calAccess === null) return CALENDAR_ACCESS_OPTIONS[0];
    if (Array.isArray(user.calAccess)
      && user.calAccess.length === 0) return CALENDAR_ACCESS_OPTIONS[2];
    if (Array.isArray(user.calAccess)
      && user.calAccess.length > 0) return CALENDAR_ACCESS_OPTIONS[1];
    return CALENDAR_ACCESS_OPTIONS[0];
  };

  const getGroups = () => {
    if (!Array.isArray(user.userGroupIds)) return [];
    return user.userGroupIds
      .map((id) => groupMap[id])
      .filter(Boolean)
      .map((g) => ({ id: g.id, name: g.name }));
  };

  const getSelectedCalendars = () => {
    if (!Array.isArray(user.calAccess)) return [];
    return user.calAccess.map((id) => {
      const cal = calendarMap[id] || accountCalendarsMap[id];
      if (!cal) return { id, name: 'Archived' };
      return cal.archived
        ? { id: cal.id, name: cal.name, archived: true }
        : { id: cal.id, name: cal.name };
    });
  };

  const getRestriction = (settingsField) => {
    if (user.userSettings != null) return user.userSettings[settingsField] === 0;
    return false;
  };

  return {
    first: user.first || '',
    last: user.last || '',
    email: (usernamesEnabled ? user.username : user.email) || '',
    ssoId: user.ssoId || '',
    mobileCountry: user.countryCode || user.phoneCountryCode || '',
    mobileNumber: user.phone || '',
    gender: (() => {
      if (!user.gender) return null;
      const names = { 1: 'Male', 2: 'Female', 3: 'Other' };
      return { id: String(user.gender), name: names[user.gender] || 'Other' };
    })(),
    externalId: user.externalId || '',
    changePassword: '',
    confirmChangePassword: '',
    groupAccess: getGroupAccessOption(),
    groups: getGroups(),
    allowAthleteCalendars: true,
    calendarAccess: getCalendarAccessOption(),
    selectedCalendars: getSelectedCalendars(),
    attendance: getRestriction('attendance'),
    exercises: getRestriction('exercises'),
    feed: getRestriction('feed'),
    manageUsers: getRestriction('manageUsers'),
    reporting: getRestriction('reporting'),
    onlinePayments: getRestriction('reseller'),
    billing: getRestriction('billing'),
    gymStudio: user.canAccessGymStudioHub === false,
  };
};

const NEW_COACH_INITIAL_VALUES = {
  first: '',
  last: '',
  email: '',
  ssoId: '',
  mobileCountry: '',
  mobileNumber: '',
  gender: null,
  externalId: '',
  changePassword: '',
  confirmChangePassword: '',
  groupAccess: GROUP_ACCESS_OPTIONS[0],
  groups: [],
  allowAthleteCalendars: true,
  calendarAccess: CALENDAR_ACCESS_OPTIONS[0],
  selectedCalendars: [],
  attendance: false,
  exercises: false,
  feed: false,
  manageUsers: false,
  reporting: false,
  onlinePayments: false,
  billing: false,
  gymStudio: false,
};

const FormWrapper = styled.div`
  width: min(850px, 100vw);
  min-width: min(600px, 100vw);
  min-height: min(800px, calc(100vh - 80px));
  max-height: calc(100vh - 150px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 8px;

  @media screen and (max-width: 767px) {
    width: 100vw;
    min-width: unset;
    min-height: calc(100vh - 80px);
    max-height: calc(100vh - 80px);
  }
`;

const FormLayout = styled(Form)`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding-bottom: 0 !important;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 12px 0 12px 20px;
  flex-shrink: 0;
`;

const FormBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 24px;

  @media (max-width: 767px) {
    padding: 16px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  width: 100%;
  flex-shrink: 0;
  padding: 16px 24px;
  background: white;
  border-top: 1px solid #EEEEEE;
  box-sizing: border-box;

  @media (max-width: 767px) {
    flex-direction: column;
    gap: 6px;
    padding: 12px 16px;
  }
`;

const SectionDivider = styled.div`
  border-top: 1px solid #EEEEEE;
  margin: 8px 0 20px;
`;

const CheckboxGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 24px;
  margin-top: 12px;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-family: "Nunito Sans";
  font-size: 14px;
  color: #333333;
  user-select: none;
`;

const StyledCheckbox = styled.input`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  cursor: pointer;
  accent-color: #444444;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-family: "Nunito Sans";
  font-size: 14px;
  color: #333333;
  margin-bottom: 16px;
  user-select: none;
`;

const AddCoachModal = ({
  isOpen, onClose, coach, onCreated, onUpdated,
}) => {
  const isEditMode = !!coach;

  const dispatch = useDispatch();
  const formikDirtyRef = useRef(false);
  const scrollRef = useRef(null);

  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const isCreatingUser = useSelector((state) => state.navigator.ui.isCreatingUser);
  const isUpdatingUser = useSelector((state) => state.navigator.ui.isUpdatingSelectedUser);
  const selectedUser = useSelector((state) => state.navigator.data.selectedUser);
  const groups = useSelector((state) => state.navigator.data.groups);
  const calendars = useSelector((state) => state.navigator.data.calendars);
  // Archived-inclusive, unlike calendars above.
  const accountCalendars = useSelector(
    (state) => state.calendars.data.unparsedCalendars,
  );
  const usernamesEnabled = useSelector(
    (state) => state.auth.data.organizationSettings?.generalSettings?.usernamesEnabled,
  );
  const pageVisibility = useSelector(
    (state) => state.auth.data.organizationSettings?.pageVisibility,
  );

  const visibleAccessRestrictionFields = ACCESS_RESTRICTION_FIELDS.filter(
    ({ visibilityKey }) => !visibilityKey || (pageVisibility?.[visibilityKey] ?? true),
  );

  const [unsavedModalOpen, setUnsavedModalOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setUnsavedModalOpen(false);
      if (isEditMode && coach?.id && currentUser?.accountCode) {
        dispatch(fetchUser(currentUser.accountCode, coach.id));
      }
    }
  }, [isOpen, coach?.id]);

  const handleCloseAttempt = () => {
    if (formikDirtyRef.current) {
      setUnsavedModalOpen(true);
    } else {
      onClose();
    }
  };

  const isBusy = isEditMode ? isUpdatingUser : isCreatingUser;
  const isLoadingEditData = isEditMode && (!selectedUser || selectedUser.id !== coach?.id);

  const initialValues = isEditMode && selectedUser && selectedUser.id === coach?.id
    ? buildEditInitialValues(
      selectedUser,
      usernamesEnabled,
      groups,
      calendars,
      accountCalendars,
    )
    : NEW_COACH_INITIAL_VALUES;

  const validationSchema = Yup.object().shape({
    first: Yup.string().required('First name is required'),
    last: Yup.string().required('Last name is required'),
    email: (() => {
      if (usernamesEnabled) {
        return Yup.string().trim().min(1, 'Username is required').required('Username is required');
      }

      if (isEditMode) {
        return Yup.string()
          .required('Email address is required')
          .test('email-format-if-changed', 'Please enter a valid email address', (value) => {
            if (value === selectedUser?.email) return true;
            return Yup.string().email().isValidSync(value);
          });
      }

      return Yup.string().email('Please enter a valid email address').required('Email address is required');
    })(),
    changePassword: usernamesEnabled && !isEditMode
      ? Yup.string()
        .required('A password is required for username-based accounts')
        .matches(complexPasswordRegex, 'Passwords must contain 8 characters, 1 lowercase, 1 uppercase, 1 number, and 1 symbol.')
      : Yup.string()
        .test('password-validation', 'Passwords must contain 8 characters, 1 lowercase, 1 uppercase, 1 number, and 1 symbol.', (value) => {
          if (!value || value === '') return true;
          return complexPasswordRegex.test(value);
        }),
    confirmChangePassword: usernamesEnabled && !isEditMode
      ? Yup.string()
        .required('Please confirm password')
        .oneOf([Yup.ref('changePassword')], 'Passwords do not match')
      : Yup.string().when('changePassword', {
        is: (val) => !!val && val.length > 0,
        then: (schema) => schema
          .required('Please confirm password')
          .oneOf([Yup.ref('changePassword')], 'Passwords do not match'),
        otherwise: (schema) => schema.oneOf([Yup.ref('changePassword'), ''], 'Passwords do not match'),
      }),
    groups: Yup.array().when('groupAccess', {
      is: (val) => val?.id === '1',
      then: (schema) => schema.min(1, 'Please select at least one group.'),
      otherwise: (schema) => schema,
    }),
  });

  const handleFormSubmit = (values, { setSubmitting }) => {
    const userGroupAccessIds = (() => {
      if (values.groupAccess?.id === '0') return null;
      if (values.groupAccess?.id === '2') return [];
      return (values.groups || []).map((g) => g.id);
    })();

    const calendarAccessIds = (() => {
      if (values.calendarAccess?.id === '0') return null;
      if (values.calendarAccess?.id === '2') return [];
      return values.calendarAccess?.id === '1' ? values.selectedCalendars.map((c) => c.id) : [];
    })();

    const restrictions = {
      attendanceOff: values.attendance,
      exercisesOff: values.exercises,
      feedOff: values.feed,
      manageUsersOff: values.manageUsers,
      reportingOff: values.reporting,
      resellerOff: values.onlinePayments,
      billingOff: values.billing,
      gymstudioOff: values.gymStudio,
      athleteCalOff: !values.allowAthleteCalendars,
    };

    const emailField = usernamesEnabled ? { username: values.email } : { email: values.email };

    if (isEditMode) {
      dispatch(updateCoach(
        currentUser.accountCode,
        coach.id,
        {
          userGroupAccessIds,
          calendarAccessIds,
          restrictions,
          user: {
            firstName: values.first,
            lastName: values.last,
            ...emailField,
            mobileNumber: values.mobileNumber !== '' && values.mobileNumber != null ? String(values.mobileNumber) : '',
            countryCode: values.mobileCountry || '',
            gender: values.gender ? Number(values.gender.id) : 0,
            ssoId: values.ssoId || '',
            externalId: values.externalId || '',
            ...(values.changePassword && { password: values.changePassword }),
          },
        },
        {
          onSuccess: (updated) => {
            showSuccessToast(`${values.first} ${values.last} has been updated.`);
            setSubmitting(false);
            if (onUpdated) onUpdated(updated);
            onClose();
          },
          onError: (err) => {
            showErrorToast(err?.response?.headers?.message || 'Unable to update coach. Please try again.');
            setSubmitting(false);
          },
        },
      ));
    } else {
      dispatch(createCoach(
        currentUser.accountCode,
        {
          userGroupAccessIds,
          calendarAccessIds,
          restrictions,
          user: {
            firstName: values.first,
            lastName: values.last,
            ...emailField,
            ...(values.mobileNumber ? { mobileNumber: String(values.mobileNumber) } : {}),
            ...(values.mobileCountry ? { countryCode: values.mobileCountry } : {}),
            gender: values.gender ? Number(values.gender.id) : 0,
            ...(values.ssoId && { ssoId: values.ssoId }),
            ...(values.externalId && { externalId: values.externalId }),
            ...(values.changePassword ? { password: values.changePassword } : {}),
          },
        },
        {
          onSuccess: (newCoach) => {
            showSuccessToast(`${values.first} ${values.last} has been added.`);
            setSubmitting(false);
            if (onCreated) onCreated(newCoach);
            onClose();
          },
          onError: (err) => {
            showErrorToast(err?.response?.headers?.message || 'Unable to add coach. Please try again.');
            setSubmitting(false);
          },
        },
      ));
    }
  };

  const athleteForPic = isEditMode ? selectedUser : { pic: DEFAULT_PIC };
  const title = isEditMode ? `${coach.first} ${coach.last}` : 'New Coach';
  const saveLabel = isEditMode ? 'Save Changes' : 'Save and Finish';

  return (
    <>
      <Modal
        isOpen={isOpen}
        onRequestClose={handleCloseAttempt}
        customBorderRadius='8px'
        customPadding='0px'
        customMaxWidth='850px'
        customMinWidth='min(600px, 100vw)'
        customButton
        widthFitContent
      >
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleFormSubmit}
          enableReinitialize
        >
          {({
            isSubmitting, values, errors, touched, handleChange, handleBlur,
            setFieldValue, dirty,
          }) => {
            formikDirtyRef.current = dirty;

            return (
              <FormWrapper>
                <FormLayout autoComplete='off' data-1p-ignore='true' data-lpignore='true'>
                  <Header>
                    <Text styledTextProps={{ fontSize: '24px', fontWeight: 700, color: '#1A1A1A' }}>
                      {title}
                    </Text>
                    <Button
                      icon='remove'
                      iconSize={15}
                      onClick={(e) => { e.preventDefault(); handleCloseAttempt(); }}
                      fontSize='13px'
                      txtColor='#9E9E9E'
                      noBorder
                    />
                  </Header>

                  {!isLoadingEditData && (
                    <>
                      <FormBody ref={scrollRef}>
                        <UserProfileFields
                          athlete={athleteForPic}
                          isNew={!isEditMode}
                          values={values}
                          errors={errors}
                          touched={touched}
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          setFieldValue={setFieldValue}
                          usernamesEnabled={usernamesEnabled}
                          selectBoxStyles={SELECTBOX_STYLES}
                          scrollContainerRef={scrollRef}
                          showPINFields={false}
                          passwordCollapsible={false}
                        />

                        {/* Group Access */}
                        <SectionDivider />
                        <Row>
                          <FieldWrapper>
                            <Text styledTextProps={{ fontSize: '16px', fontWeight: 700, marginBottom: '8px' }}>
                              Group Access
                            </Text>
                            <SelectBox
                              options={GROUP_ACCESS_OPTIONS}
                              value={values.groupAccess}
                              onChangeFunction={(opt) => setFieldValue('groupAccess', opt)}
                              isClearable={false}
                              customStyles={SELECTBOX_STYLES}
                              menuPortalTarget={document.body}
                              onMenuOpen={() => {
                                if (scrollRef?.current) scrollRef.current.style.overflowY = 'hidden';
                              }}
                              onMenuClose={() => {
                                if (scrollRef?.current) scrollRef.current.style.overflowY = 'auto';
                              }}
                            />
                          </FieldWrapper>
                        </Row>
                        {values.groupAccess?.id === '1' && (
                          <Row>
                            <FieldWrapper>
                              <SelectBox
                                title='Select Groups'
                                isMulti
                                options={groups.map(({ id, name }) => ({ id, name }))}
                                value={values.groups}
                                onChangeFunction={(opt) => setFieldValue('groups', opt)}
                                placeholder='Select groups'
                                styledProps={{ border: `1px solid ${touched.groups && errors.groups ? '#CC0404' : '#EEEEEE'}` }}
                                customStyles={SELECTBOX_STYLES}
                                titleStyledProps={{ fontWeight: 700 }}
                                menuPortalTarget={document.body}
                                onMenuOpen={() => {
                                  if (scrollRef?.current) scrollRef.current.style.overflowY = 'hidden';
                                }}
                                onMenuClose={() => {
                                  if (scrollRef?.current) scrollRef.current.style.overflowY = 'auto';
                                }}
                              />
                              {touched.groups && errors.groups && (
                                <Text styledTextProps={{
                                  fontSize: '13px', fontWeight: 700, color: '#CC0404', marginTop: '5px',
                                }}
                                >
                                  {errors.groups}
                                </Text>
                              )}
                            </FieldWrapper>
                          </Row>
                        )}
                        <CheckboxRow onClick={() => setFieldValue('allowAthleteCalendars', !values.allowAthleteCalendars)}>
                          <StyledCheckbox
                            type='checkbox'
                            checked={values.allowAthleteCalendars}
                            onChange={() => {}}
                          />
                          Allow access to assigned athlete level calendars
                        </CheckboxRow>

                        {/* Calendar Access */}
                        <SectionDivider />
                        <Row>
                          <FieldWrapper>
                            <Text styledTextProps={{ fontSize: '16px', fontWeight: 700, marginBottom: '8px' }}>
                              Calendar Access
                            </Text>
                            <SelectBox
                              options={CALENDAR_ACCESS_OPTIONS}
                              value={values.calendarAccess}
                              onChangeFunction={(opt) => setFieldValue('calendarAccess', opt)}
                              isClearable={false}
                              customStyles={SELECTBOX_STYLES}
                              menuPortalTarget={document.body}
                              onMenuOpen={() => {
                                if (scrollRef?.current) scrollRef.current.style.overflowY = 'hidden';
                              }}
                              onMenuClose={() => {
                                if (scrollRef?.current) scrollRef.current.style.overflowY = 'auto';
                              }}
                            />
                          </FieldWrapper>
                        </Row>
                        {values.calendarAccess?.id === '1' && (
                          <CalendarPicker
                            allCalendars={calendars}
                            value={values.selectedCalendars}
                            onChange={(val) => setFieldValue('selectedCalendars', val)}
                          />
                        )}

                        {/* Access Restrictions */}
                        <SectionDivider />
                        <Text styledTextProps={{ fontSize: '16px', fontWeight: 700 }}>
                          Access Restrictions
                        </Text>
                        <Text styledTextProps={{ fontSize: '13px', color: '#757575', marginTop: '4px' }}>
                          Check boxes below to disable access to specific pages
                        </Text>
                        <CheckboxGrid>
                          {visibleAccessRestrictionFields.map(({ key, label }) => (
                            <CheckboxLabel
                              key={key}
                              onClick={() => setFieldValue(key, !values[key])}
                            >
                              <StyledCheckbox
                                type='checkbox'
                                checked={values[key]}
                                onChange={() => {}}
                              />
                              {label}
                            </CheckboxLabel>
                          ))}
                        </CheckboxGrid>
                      </FormBody>

                      <ButtonContainer>
                        <Button
                          name={isSubmitting || isBusy ? 'Saving...' : saveLabel}
                          fontSize='14px'
                          txtColor='#FFF'
                          bgColor='#1A1A1A'
                          borderColor='#1A1A1A'
                          customBorderRadius='6px'
                          styledTextProps={{ fontWeight: 600 }}
                          disabled={isSubmitting || isBusy}
                        />
                      </ButtonContainer>
                    </>
                  )}
                </FormLayout>
              </FormWrapper>
            );
          }}
        </Formik>
      </Modal>

      <ConfirmModal
        isOpen={unsavedModalOpen}
        onClose={() => setUnsavedModalOpen(false)}
        onConfirm={() => { setUnsavedModalOpen(false); onClose(); }}
        title='Unsaved Changes'
        message='You have unsaved changes. Are you sure you want to leave?'
        confirmLabel='Leave'
        confirmIcon={null}
      />
    </>
  );
};

AddCoachModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  coach: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    first: PropTypes.string,
    last: PropTypes.string,
    pic: PropTypes.string,
    email: PropTypes.string,
    username: PropTypes.string,
  }),
  onCreated: PropTypes.func,
  onUpdated: PropTypes.func,
};

AddCoachModal.defaultProps = {
  coach: null,
  onCreated: null,
  onUpdated: null,
};

export default AddCoachModal;
